import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { Component, computed, EventEmitter, inject, Output, output, signal } from '@angular/core';
import { PricingModelV2Service } from '@/app/services/pricing-model-v2/pricing-model-v2.service';
import { take } from 'rxjs';
import { BasePricingModel, CreatePricingModelV2Dto, PricingModelV2Dto } from '@/app/common/general-types/pricing.dto';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormatService } from '@/app/common/format/format.service';
import { FormField, form } from '@angular/forms/signals';
import { CreateBriefService } from '@/app/pages/briefs/service/create-brief/create-brief.service';
import { FormsModule } from '@angular/forms';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'step-agency-pricing',
  imports: [
    DatePipe,
    CurrencyPipe,
    FormField,
    FormsModule,
  ],
  templateUrl: './agency-pricing.html',
  styleUrl: './agency-pricing.css',
})
export class AgencyPricing {

  @Output() modelSelected = new EventEmitter<BasePricingModel | undefined>();


  existingModel = {
    id: 0,
    name: "Select",
    benefits: [
      "Choose existing model",
      "Works same as before",
      "Limited customization",
      "No new features"
    ]
  }

  freshModel = {
    id: 1,
    name: "Custom",
    benefits: [
      "Create a new model",
      "Customize to your needs",
      "No limitations",
      "No need to wait for Trav/Oli"
    ]
  }

  currencyOptions: { code: "gbp" | "usd", label: string, symbol: string }[] = [
    { code: 'gbp', label: 'GBP', symbol: '£' },
    { code: 'usd', label: 'USD', symbol: '$' },
  ]

  loadingAvailableModels = signal<boolean>(false);
  // availableModels = signal<BasePricingModel[]>([]);
  availableModels = signal<{ pricingModel: BasePricingModel, inventor: { firebaseUid: string,firstName: string,lastName: string, photoURL: string } }[]>([]);

  searchQueryModel = signal({ query: '' });
  searchQueryForm = form(this.searchQueryModel);

  /** Models whose title contains the search query (case-insensitive); all models when query is empty */
  filteredPricingModels = computed(() => {
    const q = this.searchQueryModel().query.trim().toLowerCase();
    const models = this.availableModels();
    if (!q) {
      return models;
    }
    return models.filter((m) => (m.pricingModel.title ?? '').toLowerCase().includes(q));
  });

  /** Column indices 0..n-1 for the widest influencerLevels array in the visible (filtered) rows */
  levelColumnIndices = computed(() => {
    const models = this.filteredPricingModels();
    const max = Math.max(0, ...models.map((m) => m.pricingModel.influencerLevels?.length ?? 0));
    return Array.from({ length: max }, (_, i) => i);
  });

  /** Services */
  public currentUserService = inject(CurrentUserService);
  public pricingModelV2Service = inject(PricingModelV2Service);
  public formatService = inject(FormatService);
  public createBriefService = inject(CreateBriefService);
  private alertService = inject(AlertService);
  private firebaseService = inject(FirebaseService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  modelDefinitionType = signal<"existing" | "fresh" | undefined>(undefined);
  currency = signal<"gbp" | "usd">('gbp');
  isUgcOnly = signal<boolean>(false);
  videosPerCreator = signal<number>(1);

  ugcPrice = signal<number>(20);
  minPriceError = signal<boolean>(false);

  creatingModel = signal<boolean>(false);

  infuencerRowsStatic: {
    title: string | undefined;
    bonusValue: number | undefined;
    commission: 0;
    followersFrom: number | undefined;
    followersTo: number | undefined;
  }[] = [
    {
        "title": "Nano",
        "bonusValue": 10,
        "commission": 0,
        "followersFrom": 0,
        "followersTo": 1000
    }
]

  onUgcPriceChange(value: string | number): void {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    if (!Number.isNaN(n)) {
      this.ugcPrice.set(n);
    }
  }

  selectMethod(modelId: number) {
    if (modelId === 0) {
      this.modelDefinitionType.set('existing');
      this.getExistingModels();
    } else {
      this.modelDefinitionType.set('fresh');
    }
  }

  getExistingModels() {
    console.log('getExistingModels()');
    this.loadingAvailableModels.set(true);
    // this.pricingModelV2Service.getAgencyPricingModels(this.currentUserService.focussedAgency.agency.id).pipe(take(1)).subscribe((res) => {
    //   console.log('Existing Models: ', res);
    //   this.availableModels.set(res);
    //   this.loadingAvailableModels.set(false);
    // });
    this.pricingModelV2Service.getAgencyPricingModelsInventor(this.currentUserService.focussedAgency.agency.id).pipe(take(1)).subscribe((res) => {
      console.log('Existing Models: ', res);
      this.availableModels.set(res);
      this.loadingAvailableModels.set(false);
    });
  }

  selectModel(model: BasePricingModel) {
    console.log('selectModel()', model);
    this.createBriefService.selectedPricingModel.set(model);
    this.modelSelected.emit(model);
  }

  getUgcPrice(model: PricingModelV2Dto) {
    /**
     * face, 30 sec
     */
    let total = model.ugcPrice + model.thirtySecPrice + model.facePrice
    return total / 100;
  }

  incrementVideosPerCreator(value: number) {
    const next = this.videosPerCreator() + value;
    const clamped = Math.min(4, Math.max(1, next));
    this.videosPerCreator.set(clamped);
  }

  getCurrencyClass(currency: { code: "gbp" | "usd", label: string }) {
    if (this.currency() === currency.code) {
      return 'bg-primary text-white hover:bg-primary/90'
    } else {
      return 'bg-zinc-300 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 hover:bg-primary/90'
    }
  }

  getIsUgcOnlyClass(btn: boolean) {
    if (btn === this.isUgcOnly()) {
      return 'bg-primary text-white hover:bg-primary/90'
    } else {
      return 'bg-zinc-300 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 hover:bg-primary/90'
    }
  }

  getRowTotal(row: {
    title: string | undefined;
    bonusValue: number | undefined;
    commission: 0;
    followersFrom: number | undefined;
    followersTo: number | undefined;
  }) {
    if (row.bonusValue && this.ugcPrice()) {
      return (row.bonusValue + this.ugcPrice());
    } else {
      return 0
    }
  }

  /** Parses follower inputs from ngModel; undefined if empty/invalid. */
  private parseFollowerInt(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
    return Number.isFinite(n) && !Number.isNaN(n) ? n : undefined;
  }

  /**
   * Keeps tiers contiguous: row[i-1].followersTo + 1 === row[i].followersFrom.
   * When min followers on row i changes, previous row's max becomes min - 1.
   */
  onInfluencerFollowersFromChange(index: number, value: string | number | null | undefined): void {
    const from = this.parseFollowerInt(value);
    if (from === undefined) {
      return;
    }
    const rows = this.infuencerRowsStatic;
    const row = rows[index];
    row.followersFrom = from;

    if (index > 0) {
      const prev = rows[index - 1];
      prev.followersTo = from - 1;
      if (prev.followersFrom !== undefined && prev.followersFrom > prev.followersTo!) {
        prev.followersFrom = prev.followersTo!;
      }
    }

    if (row.followersTo !== undefined && row.followersTo < from) {
      row.followersTo = from + 999;
    }
  }

  /**
   * When max followers on row i changes, the next row's min becomes max + 1
   * (e.g. row[0].followersTo = 300 → row[1].followersFrom = 301).
   */
  onInfluencerFollowersToChange(index: number, value: string | number | null | undefined): void {
    const to = this.parseFollowerInt(value);
    if (to === undefined) {
      return;
    }
    const rows = this.infuencerRowsStatic;
    const row = rows[index];
    row.followersTo = to;

    if (row.followersFrom !== undefined && to < row.followersFrom) {
      row.followersFrom = to;
    }

    if (index < rows.length - 1) {
      const next = rows[index + 1];
      next.followersFrom = to + 1;
      if (next.followersTo !== undefined && next.followersTo < next.followersFrom!) {
        next.followersTo = next.followersFrom! + 999;
      }
    }
  }

  removeInfluencerRowStatic(index: number) {
    if (index > 0 && this.infuencerRowsStatic[index + 1] && this.infuencerRowsStatic[index - 1]) {
      const prevRow = this.infuencerRowsStatic[index - 1];
      this.infuencerRowsStatic[index + 1] = {
        ...this.infuencerRowsStatic[index + 1],
        followersFrom: (prevRow.followersTo ?? 0) + 1,
      };
    }
    this.infuencerRowsStatic.splice(index, 1);
  }

  addInfluencerRowStatic() {
    console.log('addInfluencerRowStatic()');
    console.log('infuencerRowsStatic BEFORE: ', this.infuencerRowsStatic);
    const lastRow = JSON.parse(JSON.stringify(this.infuencerRowsStatic[this.infuencerRowsStatic.length - 1]));
    console.log('lastRow: ', lastRow);
    this.infuencerRowsStatic.push({
      title: this.getDeafultInfluencerTitle(this.infuencerRowsStatic.length),
      bonusValue: lastRow.bonusValue! + 10,
      commission: 0,
      followersFrom: lastRow.followersTo! + 1,
      followersTo: lastRow.followersTo! + 1000,
    })
    console.log('infuencerRowsStatic AFTER: ', this.infuencerRowsStatic);
  }

  getDeafultInfluencerTitle(newRowIndex: number): string {
    const defaultTitles: string[] = ['Nano', 'Micro', 'Mid-Tier', 'Macro', 'Mega'];
    if (newRowIndex < defaultTitles.length) {
      return defaultTitles[newRowIndex];
    }
    return `Tier ${newRowIndex + 1}`;
  }

  canAddInfluencerRow(): boolean {

    if (this.ugcPrice() < 20) {
      this.minPriceError.set(true);
      return false;
    } else {
      this.minPriceError.set(false);
    }
    /**
     * 1. Check if all array items are fully defined
     */
    return this.infuencerRowsStatic.every(
      (row) =>
        row.title &&
        row.bonusValue &&
        row.followersFrom !== undefined &&
        row.followersTo !== undefined &&
        row.followersTo > row.followersFrom,
    );
  }

  async lockPrices() {
    console.log('lockPrices()');

    const confirm = await this.alertService.confirm({
      title: 'Are you sure?',
      message: "Once you've locked in prices, you won't be able to change them.",
      confirmText: 'Lock Prices',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    console.log('confirm: ', confirm);
    if (confirm) {
      console.log('Please lock them in...');
      this.submitNewModel();
    }
  }

  submitNewModel() {
    console.log('submitNewModel()');
    console.log('infuencerRows: ', this.infuencerRowsStatic);
    this.creatingModel.set(true);

    // 1. Create Pricing Model
    let newPricingModel: CreatePricingModelV2Dto = {
      isCurrent: false,
      createdBy: this.firebaseService.userId!,
      title: this.getPricingModelTitle(),
      businessAccountId: this.currentUserService.focussedBrand!.id,
      agencyId: this.currentUserService.focussedAgency.agency.id,
      currencyCode: this.currency() === 'gbp' ? 'gbp' : 'usd',
      partnerContribution: 0,
      fifteenSecPrice: 100,
      thirtySecPrice: 100,
      sixtySecPrice: 100,
      facePrice: 0,
      ugcPrice: (this.ugcPrice() * 100) - 100,
      advertisingPrice: 0,
      socialPostPrice: (this.ugcPrice() * 100) - 100,
      commissionPercentage: 0,
      licenseCommissionPercentage: 0,
      ranks: [{ rank: 0, multiplier: 1 }],
      influencerLevels: this.infuencerRowsStatic.map((row) => ({
        title: row.title!,
        followersFrom: row.followersFrom!,
        followersTo: row.followersTo!,
        bonusValue: row.bonusValue! * 100,
        commission: row.commission!
      })),
      charityDiscountPercentage: 0
    }
    console.log("newPricingModel: ", newPricingModel);

    this.pricingModelV2Service.createPricingModel(newPricingModel).subscribe((res) => {
      console.log('Pricing model created: ', res);
      this.creatingModel.set(false);
      this.toastNotificationService.alert({
        title: 'Pricing model created!',
        message: 'The pricing model has been created successfully.',
        type: 'success',
        icon: 'success',
      });
      this.modelSelected.emit(res);
    }, (error) => {
      console.error('Error creating pricing model: ', error);
      this.creatingModel.set(false);
      this.toastNotificationService.alert({
        title: 'Error creating pricing model!',
        message: 'Something went wrong. Please try again.',
        type: 'error',
        icon: 'error',
      });
    });
  }

  getPricingModelTitle() {
    const brandName = this.currentUserService.focussedBrand!.companyName;
    const brandInitials = brandName.split(' ').map((name: string) => name[0]).join('');
    const date = new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
    return `${brandInitials} - ${date}`;
  }
}
